import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, HelpCircle, Store } from 'lucide-react';
import { npcShopPath, priceLabel, sellersOf } from '../../utils/npcShops';
import { WorldSection } from './WorldPageHeader';
import { SourceCard } from './SourceCard';
import { BagSourceList } from './BagSourceList';
import { DataStatus, missingFiles } from './DataStatus';
import { groupLabel, playerItemSources, qualityLabel, routeQuality, sourceGroup } from '../../utils/itemSources';
import type { Quality, ResolvedSource, SourceGroup } from '../../utils/itemSources';
import { isOpenableBag, playerBagSources } from '../../utils/bagSources';
import type { BagSource } from '../../utils/bagSources';
import { recipesProducing } from '../../utils/recipeLinks';
import type { WorldData } from '../../types/world';
const GROUPS: SourceGroup[] = ['drop', 'event', 'box', 'quest', 'other'];
const QUALITIES: Quality[] = ['normal', 'excellent', 'ancient', 'other'];

/**
 * "Dónde conseguirlo": todas las formas de obtención documentadas, evaluadas ruta por ruta y
 * separadas por tipo (drop, invasión/evento, caja, misión) y calidad (normal, Excellent, Ancient).
 * Las asociaciones retenidas por conflicto no aparecen; las rutas desactivadas van al final.
 */
export function WhereToGet({
  w,
  id,
  name
}: {
  w: WorldData;
  id: string;
  name: string;
}) {
  const all = useMemo(() => playerItemSources(w, id), [w, id]);
  const active = all.filter(s => s.status !== 'disabled');
  const disabled = all.filter(s => s.status === 'disabled');
  const bags = useMemo(() => {
    const viaRoutes = new Set(active.map(s => s.route.bagId).filter(Boolean));
    return playerBagSources(w, id).filter(b => !viaRoutes.has(b.bag.id));
  }, [w, id, active]);
  const crafted = useMemo(() => recipesProducing(id, name), [id, name]);
  const sellers = useMemo(() => sellersOf(id), [id]);
  const [showDisabled, setShowDisabled] = useState(false);
  const missingRoutes = missingFiles(w, ['routes', 'routeTargets']);
  const missingBags = missingFiles(w, ['bags', 'bagTriggers', 'bagRewards']);
  const grouped = GROUPS.map(g => ({
    g,
    sources: active.filter(s => sourceGroup(s) === g),
    bags: g === 'box' ? bags.filter(isOpenableBag) : g === 'event' ? bags.filter(b => !isOpenableBag(b)) : [] as BagSource[]
  })).filter(x => x.sources.length || x.bags.length);
  const located = active.some(s => s.eventMaps.length || s.eventMapId !== undefined || s.targets.some(t => t.spawns.length));
  const documented = active.some(s => !s.pending) || bags.length > 0;
  const total = active.length + bags.length;
  return <WorldSection id="donde-conseguirlo" title="Dónde conseguirlo" count={total + crafted.length + sellers.length} data-id="element-2042" data-mp="2zxbsw41dfnw4">
      <div className="-mt-1 mb-3 flex flex-wrap gap-1.5" data-id="element-2043" data-mp="34rocce2ukz7v">
        {missingRoutes.length ? <DataStatus kind="unchecked" data-id="element-2044" data-mp="279xt7y6age8b" /> : <>
            {documented && <DataStatus kind="route" data-id="element-2045" data-mp="2c7puvjwqjepp" />}
            {located && <DataStatus kind="location" withDate={false} data-id="element-2046" data-mp="24pj8vomcf0bt" />}
            {active.some(s => s.pending) && <DataStatus kind="partial" data-id="element-2047" data-mp="1zqy8np73cgnd" />}
          </>}
      </div>

      {missingRoutes.length > 0 && <p className="mb-3 flex items-start gap-2 rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm text-gray-300" data-id="element-2048" data-mp="1ur2cud8jrviw">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2049" data-mp="1qoyqgtkcb6iu" />
          No comprobado: no se pudieron cargar las rutas de obtención. Esto no significa que el ítem no tenga drops.
        </p>}

      {!missingRoutes.length && total === 0 && crafted.length === 0 && sellers.length === 0 && <p className="text-gray-400" data-id="element-2050" data-mp="1c1do51vahbbz">{disabled.length ? 'Ninguna forma de obtención está disponible en la configuración actual.' : 'Ubicación de obtención pendiente de confirmar.'}</p>}

      <div className="space-y-6" data-id="element-2051" data-mp="12zwcbvowzvt3">
        {grouped.map(({
        g,
        sources,
        bags: gb
      }) => {
        const qs = QUALITIES.filter(q => sources.some(s => routeQuality(s.route, w) === q));
        return <div key={g} data-id="element-2052" data-mp="2y4dt8lzgy669">
              <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-2053" data-mp="325641jnzoj4n">{groupLabel[g]}</h3>
              {qs.map(q => <div key={q} className="mb-3 last:mb-0" data-id="element-2054" data-mp="2oczo9yor0e5q">
                  {(qs.length > 1 || q !== 'normal') && <p className="mb-2 text-xs font-semibold text-gray-300" data-id="element-2055" data-mp="2tbu893ygc8rf">{qualityLabel[q]}</p>}
                  <ul className="grid gap-3" data-id="element-2056" data-mp="2eejv7q65uutl">
                    {sources.filter(s => routeQuality(s.route, w) === q).map(s => <SourceCard key={s.key} w={w} s={s} data-id="element-2057" data-mp="2jfhgibmgw4zb" />)}
                  </ul>
                </div>)}
              {gb.length > 0 && <div className={sources.length ? 'mt-3' : ''} data-id="element-2058" data-mp="2adq7awspxg0p">
                  {missingBags.length > 0 ? <DataStatus kind="unchecked" data-id="element-2059" data-mp="3nk07bgwz3js" /> : <BagSourceList w={w} sources={gb} data-id="element-2060" data-mp="3oledot8865q" />}
                </div>}
            </div>;
      })}

        {sellers.length > 0 && <div data-id="element-2061" data-mp="iql7bero3tzw">
            <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-2062" data-mp="iq1ycnjzwfy0">Comprar a un NPC</h3>
            <ul className="grid gap-2" data-id="element-2063" data-mp="ds9zf9kxntkb">
              {sellers.map(({
            npc,
            item
          }) => <li key={`${npc.id}-${item.itemId}-${item.level ?? ''}`} data-id="element-2064" data-mp="rj592o39aeoy">
                  <Link to={npcShopPath(npc.id)} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-2065" data-mp="ms6rll6j7ak4">
                    <Store className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-2066" data-mp="whymyhmgg7pn" />
                    <span className="min-w-0" data-id="element-2067" data-mp="wkckcx4lxw0d">
                      <span className="block font-semibold text-white" data-id="element-2068" data-mp="1agpzhzlo5lbo">{npc.name}</span>
                      <span className="block text-xs text-gray-400" data-id="element-2069" data-mp="16fwbz7c528jb">
                        {[item.level !== undefined ? `+${item.level}` : '', priceLabel(item) ?? 'Precio no documentado', 'Ver ubicación del vendedor'].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>}

        {crafted.length > 0 && <div data-id="element-2070" data-mp="1g681b6hkv6r5">
            <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-2071" data-mp="1g7svguzzcnoh">Fabricación</h3>
            {total === 0 && <p className="mb-2 text-sm text-gray-300" data-id="element-2072" data-mp="1q4ov0b7whzgw">No tiene drops documentados: se obtiene combinando materiales.</p>}
            <ul className="grid gap-2" data-id="element-2073" data-mp="1u4pmawg3m55d">
              {crafted.map(r => <li key={r.id} data-id="element-2074" data-mp="1z4bx6z8gf9b0">
                  <a href="#como-crearlo" className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-2075" data-mp="1z3iy8kfqlo3f">
                    <FlaskConical className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-2076" data-mp="28tlt6q6f5c4e" />
                    <span className="min-w-0" data-id="element-2077" data-mp="2duro8unjxbh3">
                      <span className="block font-semibold text-white" data-id="element-2078" data-mp="2drx3f20mlon5">{r.title} · {r.machine ?? 'Chaos Machine'}</span>
                      <span className="block text-xs text-gray-400" data-id="element-2079" data-mp="67q2zftsknyr">Ver materiales y pasos más abajo · receta clásica, pendiente de validar en Vanguard</span>
                    </span>
                  </a>
                </li>)}
            </ul>
          </div>}

        {disabled.length > 0 && <div data-id="element-2080" data-mp="3oee6j197132b">
            <button type="button" onClick={() => setShowDisabled(v => !v)} aria-expanded={showDisabled} className="h-10 text-sm font-semibold text-gray-400 hover:text-white" data-id="element-2081" data-mp="3oewciplnqvnf">
              {showDisabled ? 'Ocultar' : 'Ver'} formas no disponibles ahora ({disabled.length})
            </button>
            {showDisabled && <ul className="mt-2 grid gap-3" data-id="element-2082" data-mp="g4kzjt6i8eae">{disabled.map((s: ResolvedSource) => <SourceCard key={s.key} w={w} s={s} data-id="element-2083" data-mp="b7ul1aaclqr3" />)}</ul>}
          </div>}
      </div>
    </WorldSection>;
}